import {
  Button,
  cn,
  FormItem,
  Input,
  Label,
  TextField,
} from '@lemonenergy/lemonpie-3'
import type {
  ActionFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
} from '@remix-run/node'
import { redirect } from '@remix-run/node'
import {
  Form,
  useActionData,
  useNavigation,
  useSearchParams,
} from '@remix-run/react'
import posthog from 'posthog-js'
import type { ComponentProps } from 'react'
import { useEffect } from 'react'
import { z } from 'zod'

import ErrorHintText from '~/components/ui/ErrorHintText'
import { PasswordField } from '~/components/ui/PasswordField'
import { captureLoginEvent } from '~/controllers/loginTracking.controller.server'
import { getAuthorizedUserFromRequest } from '~/controllers.server/auth'
import usePosthogInitialized from '~/hooks/usePosthogInitialized'
import constants from '~/res/constants/constants.server'
import { AuthPage } from '~/routes/login/components/AuthPage'
import {
  authenticateWithCredentials,
  completeNewPassword,
  serializeAuthCookies,
} from '~/utils/auth.util/auth.service.server'
import { getFormString } from '~/utils/form.util/formInputs'
import { jsonResponse } from '~/utils/response.util'

export { default as ErrorBoundary } from '~/components/ErrorBoundary'

export const links: LinksFunction = () => {
  return [
    {
      href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap',
      rel: 'stylesheet',
    },
  ]
}

const credentialsSchema = z.object({
  username: z.string().min(1, 'Informe seu e-mail ou usuário'),
  password: z.string().min(1, 'Informe sua senha'),
})

const newPasswordSchema = z
  .object({
    username: z.string().min(1),
    session: z.string().min(1),
    newPassword: z.string().min(8, 'A senha deve ter ao menos 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirme a nova senha'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'As senhas não coincidem',
  })

export const loader = async ({ request }: LoaderFunctionArgs) => {
  if (!process.env.COGNITO_CLIENT_ID) {
    throw new Error('COGNITO_CLIENT_ID is not defined')
  }

  const url = new URL(request.url)
  const redirectTo = url.searchParams.get('redirectTo') || '/'

  const user = await getAuthorizedUserFromRequest(request)

  if (user) {
    return redirect(redirectTo)
  }

  return {}
}

const commitAndRedirect = async (
  tokens: { idToken?: string; accessToken: string },
  redirectTo: string,
) => {
  const cookie = await serializeAuthCookies({
    authCookie: constants.cookies.authentication,
    tokens,
  })

  return redirect(redirectTo, {
    headers: { 'set-cookie': cookie },
  })
}

const handleNewPassword = async (
  formData: FormData,
  redirectTo: string,
  context: ActionFunctionArgs['context'],
) => {
  const parsed = newPasswordSchema.safeParse({
    username: formData.get('username'),
    session: formData.get('session'),
    newPassword: formData.get('newPassword'),
    confirmPassword: formData.get('confirmPassword'),
  })

  const challenge = {
    username: getFormString(formData, 'username'),
    session: getFormString(formData, 'session'),
  }

  if (!parsed.success) {
    return jsonResponse(
      {
        step: 'newPassword' as const,
        challenge,
        errors: parsed.error.flatten().fieldErrors,
        error: null,
      },
      { status: 400 },
    )
  }

  try {
    const { tokens } = await completeNewPassword({
      username: parsed.data.username,
      newPassword: parsed.data.newPassword,
      session: parsed.data.session,
      cognitoClientId: process.env.COGNITO_CLIENT_ID,
      cognitoClientSecret: process.env.COGNITO_CLIENT_SECRET,
    })

    await captureLoginEvent({
      context,
      event: 'login_succeeded',
      properties: { via: 'new_password' },
    })

    return commitAndRedirect(tokens, redirectTo)
  } catch (e) {
    const name = e instanceof Error ? e.name : ''

    await captureLoginEvent({
      context,
      event: 'login_failed',
      properties: { via: 'new_password', reason: name || 'unknown' },
    })

    // eslint-disable-next-line no-console
    console.log(new Error('Error while setting new password', { cause: e }))

    let error = 'Não foi possível definir a nova senha. Tente novamente.'
    if (name === 'InvalidPasswordException') {
      error = 'A senha não atende aos requisitos de segurança.'
    } else if (name === 'NotAuthorizedException') {
      // Session expired: send the user back to the credentials step.
      return jsonResponse(
        {
          step: 'login' as const,
          errors: null,
          error: 'Sessão expirada. Entre novamente.',
          challenge: null,
        },
        { status: 401 },
      )
    }

    return jsonResponse(
      { step: 'newPassword' as const, challenge, errors: null, error },
      { status: 400 },
    )
  }
}

export const action = async ({ request, context }: ActionFunctionArgs) => {
  const url = new URL(request.url)
  const redirectTo = url.searchParams.get('redirectTo') || '/'

  const formData = await request.formData()

  if (formData.get('intent') === 'newPassword') {
    return handleNewPassword(formData, redirectTo, context)
  }

  const parsed = credentialsSchema.safeParse({
    username: formData.get('username'),
    password: formData.get('password'),
  })

  if (!parsed.success) {
    return jsonResponse(
      {
        step: 'login' as const,
        errors: parsed.error.flatten().fieldErrors,
        error: null,
        challenge: null,
      },
      { status: 400 },
    )
  }

  await captureLoginEvent({ context, event: 'login_submitted' })

  try {
    const result = await authenticateWithCredentials({
      username: parsed.data.username,
      password: parsed.data.password,
      cognitoClientId: process.env.COGNITO_CLIENT_ID,
      cognitoClientSecret: process.env.COGNITO_CLIENT_SECRET,
    })

    if (result.status === 'new_password_required') {
      await captureLoginEvent({
        context,
        event: 'login_new_password_challenge',
      })

      return jsonResponse({
        step: 'newPassword' as const,
        challenge: { session: result.session, username: result.username },
        errors: null,
        error: null,
      })
    }

    await captureLoginEvent({ context, event: 'login_succeeded' })

    return commitAndRedirect(result, redirectTo)
  } catch (e) {
    const name = e instanceof Error ? e.name : ''
    const isInvalidCredentials =
      name === 'NotAuthorizedException' || name === 'UserNotFoundException'

    await captureLoginEvent({
      context,
      event: 'login_failed',
      properties: {
        reason: name || 'unknown',
        invalidCredentials: isInvalidCredentials,
      },
    })

    // eslint-disable-next-line no-console
    console.log(
      new Error('Error while authenticating with credentials', { cause: e }),
    )

    return jsonResponse(
      {
        step: 'login' as const,
        errors: null,
        error: isInvalidCredentials
          ? 'E-mail ou senha inválidos.'
          : 'Não foi possível entrar. Tente novamente.',
        challenge: null,
      },
      { status: 401 },
    )
  }
}

const AuthCard = ({
  title,
  children,
}: ComponentProps<'div'> & { title: string }) => (
  <AuthPage>
    <div className="grow grid grid-cols-12 items-center justify-center min-w-full">
      <div
        className={cn(
          'col-span-12 rounded-smaller py-[2.5rem] px-larger bg-main fg-expressive-strong',
          'sm:col-span-8 sm:col-start-3 md:col-span-6 md:col-start-4 lg:col-span-4 lg:col-start-5',
        )}
      >
        <h4 className="typography-subheading font-semibold mb-larger text-center">
          {title}
        </h4>
        {children}
      </div>
    </div>
  </AuthPage>
)

const FormError = ({ message }: { message?: string | null }) =>
  message ? (
    <p
      className="typography-body-small font-semibold fg-critical mb-main text-center"
      data-testid="credentials-error"
      role="alert"
    >
      {message}
    </p>
  ) : null

const LoginWithPassword = () => {
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const [searchParams] = useSearchParams()

  const posthogInitialized = usePosthogInitialized()

  const isSubmitting = navigation.state !== 'idle'
  const redirectTo = searchParams.get('redirectTo') || ''

  useEffect(() => {
    if (posthogInitialized) {
      posthog.capture('login_page_viewed', { method: 'password' })
    }
  }, [posthogInitialized])

  const redirectToInput = redirectTo ? (
    <input type="hidden" name="redirectTo" value={redirectTo} />
  ) : null

  if (actionData?.step === 'newPassword' && actionData.challenge) {
    return (
      <AuthCard title="Defina uma nova senha">
        <p className="typography-body-small mb-main text-center">
          Primeiro acesso: crie uma senha para continuar.
        </p>

        <FormError message={actionData.error} />

        <Form method="post" className="flex flex-col gap-main">
          <input type="hidden" name="intent" value="newPassword" />
          <input
            type="hidden"
            name="username"
            value={actionData.challenge.username}
          />
          <input
            type="hidden"
            name="session"
            value={actionData.challenge.session}
          />
          {redirectToInput}

          <PasswordField
            id="newPassword"
            name="newPassword"
            label="Nova senha"
            autoComplete="new-password"
            error={actionData.errors?.newPassword?.[0]}
          />

          <PasswordField
            id="confirmPassword"
            name="confirmPassword"
            label="Confirmar nova senha"
            autoComplete="new-password"
            error={actionData.errors?.confirmPassword?.[0]}
          />

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            data-testid="new-password-submit"
            className="mt-smaller"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar e entrar'}
          </Button>
        </Form>
      </AuthCard>
    )
  }

  const usernameError = actionData?.errors?.username?.[0]
  const passwordError = actionData?.errors?.password?.[0]

  return (
    <AuthCard title="Entrar com e-mail e senha">
      <FormError message={actionData?.error} />

      <Form method="post" className="flex flex-col gap-main">
        {redirectToInput}

        <FormItem state={usernameError ? 'error' : undefined}>
          <TextField>
            <Label htmlFor="username">E-mail ou usuário</Label>
            <Input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
            />
          </TextField>
          <ErrorHintText error={usernameError} />
        </FormItem>

        <PasswordField
          id="password"
          name="password"
          label="Senha"
          autoComplete="current-password"
          error={passwordError}
        />

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          data-testid="credentials-submit"
          className="mt-smaller"
        >
          {isSubmitting ? 'Entrando...' : 'Entrar'}
        </Button>
      </Form>
    </AuthCard>
  )
}

export default LoginWithPassword
